import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getRoleById } from '../utils/roles';
import { attendanceAPI } from '../services/api';
import './Header.css';

const Header = ({ toggleSidebar, currentModule }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { currentUser } = useAuth();
  const userRole = getRoleById(currentUser?.role);

  // Attendance state
  const [todayStatus, setTodayStatus] = useState(null); // null = loading
  const [checkLoading, setCheckLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchToday = useCallback(async () => {
    try {
      const res = await attendanceAPI.getToday();
      setTodayStatus(res);
    } catch {
      setTodayStatus({ checkedIn: false, checkedOut: false, data: null });
    }
  }, []);

  useEffect(() => {
    fetchToday();
  }, [fetchToday]);

  const handleCheckIn = async () => {
    setCheckLoading(true);
    try {
      await attendanceAPI.checkIn({ location: 'Office', isRemote: false });
      showToast('✅ Login successful! Have a great day.');
      await fetchToday();
    } catch (err) {
      const msg = err?.response?.data?.message || '';
      if (msg.toLowerCase().includes('pehle se') || msg.toLowerCase().includes('already')) {
        showToast('⚠️ Aap aaj already login hain', 'warn');
      } else {
        showToast('❌ Login failed: ' + (msg || 'Try again'), 'error');
      }
    } finally {
      setCheckLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setCheckLoading(true);
    try {
      const res = await attendanceAPI.checkOut();
      const hours = res?.data?.workHours || res?.workHours;
      showToast(`✅ Logout successful! Aaj aapne ${hours ? hours + ' ghante' : ''} kaam kiya.`);
      await fetchToday();
    } catch (err) {
      const msg = err?.response?.data?.message || '';
      if (msg.toLowerCase().includes('pehle se') || msg.toLowerCase().includes('already')) {
        showToast('⚠️ Aap aaj already logout kar chuke hain', 'warn');
      } else if (msg.toLowerCase().includes('check-in') || msg.toLowerCase().includes('login')) {
        showToast('⚠️ Pehle login karein', 'warn');
      } else {
        showToast('❌ Logout failed: ' + (msg || 'Try again'), 'error');
      }
    } finally {
      setCheckLoading(false);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderAttendanceBtn = () => {
    if (todayStatus === null) return null; // loading silently

    const { checkedIn, checkedOut, data } = todayStatus;

    if (!checkedIn) {
      const isEvening = new Date().getHours() >= 17;
      return (
        <div className="att-btn-wrap">
          {isEvening && (
            <span className="att-warn-badge">
              <i className="fas fa-exclamation-triangle"></i> Aaj login nahi kiya
            </span>
          )}
          <button
            className="att-btn att-btn-login"
            onClick={handleCheckIn}
            disabled={checkLoading}
          >
            {checkLoading
              ? <i className="fas fa-spinner fa-spin"></i>
              : <><i className="fas fa-sign-in-alt"></i> Login</>
            }
          </button>
        </div>
      );
    }

    if (checkedIn && !checkedOut) {
      return (
        <div className="att-btn-wrap">
          <span className="att-time-badge">
            <i className="fas fa-clock"></i> Logged in at {fmtTime(data?.checkIn)}
          </span>
          <button
            className="att-btn att-btn-logout"
            onClick={handleCheckOut}
            disabled={checkLoading}
          >
            {checkLoading
              ? <i className="fas fa-spinner fa-spin"></i>
              : <><i className="fas fa-sign-out-alt"></i> Logout</>
            }
          </button>
        </div>
      );
    }

    // Both done
    return (
      <div className="att-btn-wrap">
        <span className="att-done-badge">
          <i className="fas fa-check-circle"></i> Aaj ka kaam complete
          {data?.workHours ? ` — ${data.workHours}h` : ''}
        </span>
      </div>
    );
  };

  return (
    <>
      <header className="header">
        <div className="header-left">
          <button className="menu-toggle" onClick={toggleSidebar}>
            <i className="fas fa-bars"></i>
          </button>
          <h1>{currentModule}</h1>
        </div>
        <div className="header-right">
          {renderAttendanceBtn()}
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="notifications">
            <i className="fas fa-bell"></i>
            <span className="badge">5</span>
          </div>
          <div className="user-profile">
            {currentUser?.profileImage ? (
              <img src={currentUser.profileImage} alt="User" />
            ) : (
              <div className="user-avatar-circle">
                {(currentUser?.name || 'A').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="user-info">
              <span className="user-name">{currentUser?.name || 'Admin'}</span>
              <span className="user-role">{userRole?.name || 'Administrator'}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Toast notification */}
      {toast && (
        <div className={`header-toast header-toast-${toast.type}`}>
          {toast.msg}
        </div>
      )}
    </>
  );
};

export default Header;

